import config from '../config';
import selectedEnvironment from '../env';
import AuthService from './auth.service';
const API_URL = config[selectedEnvironment].API_URL;

/** Virtual-hosted-style: bucket.s3.region.amazonaws.com */
function extractBucketFromVirtualHostedS3Url(s3Url) {
    try {
        const u = new URL(s3Url);
        const h = u.hostname;
        const idx = h.toLowerCase().indexOf('.s3.');
        if (idx <= 0) return null;
        return h.substring(0, idx);
    } catch {
        return null;
    }
}

function bucketQueryParam(s3Url, explicitBucket) {
    const b = explicitBucket ?? extractBucketFromVirtualHostedS3Url(s3Url);
    return b ? `&bucket=${encodeURIComponent(b)}` : '';
}

class S3UtilService {
    /** @param {string} s3Url */
    static extractBucketFromVirtualHostedS3Url(s3Url) {
        return extractBucketFromVirtualHostedS3Url(s3Url);
    }

    /**
     * Downloads a file from S3 through the backend proxy
     * @param {string} s3Url - The S3 pre-signed URL
     * @param {string} [bucket] - S3 bucket (optional; inferred from virtual-hosted URL when omitted)
     * @returns {Promise<Object>} - Object containing file data and metadata
     */
    static async downloadFile(s3Url, bucket = undefined) {
        try {
            const token = await AuthService.getCurrentToken();
            const encodedUrl = encodeURIComponent(s3Url);
            const bq = bucketQueryParam(s3Url, bucket);
            
            console.log('S3Util: Downloading file from:', s3Url);
            console.log('S3Util: API URL:', `${API_URL}/S3Util/download-blob?url=${encodedUrl}${bq}`);
            
            const response = await fetch(`${API_URL}/S3Util/download-blob?url=${encodedUrl}${bq}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            console.log('S3Util: Response status:', response.status);
            console.log('S3Util: Response headers:', response.headers);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('S3Util: Response error:', errorText);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const result = await response.json();
            console.log('S3Util: Download result:', {
                fileName: result.fileName,
                contentType: result.contentType,
                size: result.size,
                dataLength: result.data ? result.data.length : 0
            });
            
            return result;
        } catch (error) {
            console.error('Error downloading file from S3:', error);
            throw error;
        }
    }

    /**
     * Downloads a file and returns it as a blob
     * @param {string} s3Url - The S3 pre-signed URL
     * @param {string} [bucket] - S3 bucket (optional; inferred from virtual-hosted URL when omitted)
     * @returns {Promise<Blob>} - The file as a blob
     */
    static async downloadFileAsBlob(s3Url, bucket = undefined) {
        try {
            const result = await this.downloadFile(s3Url, bucket);
            
            // Convert base64 back to blob
            const binaryString = atob(result.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            
            return new Blob([bytes], { type: result.contentType });
        } catch (error) {
            console.error('Error converting file to blob:', error);
            throw error;
        }
    }

    /**
     * Downloads a file and returns it as a File object
     * @param {string} s3Url - The S3 pre-signed URL
     * @param {string} [bucket] - S3 bucket (optional; inferred from virtual-hosted URL when omitted)
     * @returns {Promise<File>} - The file as a File object
     */
    static async downloadFileAsFile(s3Url, bucket = undefined) {
        try {
            console.log('S3Util: Starting downloadFileAsFile for:', s3Url);
            
            // Try the blob approach first
            try {
                const result = await this.downloadFile(s3Url, bucket);
                
                console.log('S3Util: Converting base64 to blob, data length:', result.data.length);
                
                // Convert base64 back to blob
                const binaryString = atob(result.data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                
                console.log('S3Util: Created bytes array, length:', bytes.length);
                
                const blob = new Blob([bytes], { type: result.contentType });
                console.log('S3Util: Created blob, size:', blob.size, 'type:', blob.type);
                
                const file = new File([blob], result.fileName, { type: result.contentType });
                console.log('S3Util: Created file:', file.name, 'size:', file.size, 'type:', file.type);
                
                return file;
            } catch (blobError) {
                console.warn('S3Util: Blob approach failed, trying direct download:', blobError);
                
                // Fallback: Use direct download approach
                const token = await AuthService.getCurrentToken();
                const encodedUrl = encodeURIComponent(s3Url);
                const bq = bucketQueryParam(s3Url, bucket);
                
                const response = await fetch(`${API_URL}/S3Util/download?url=${encodedUrl}${bq}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const blob = await response.blob();
                const fileName = s3Url.split('/').pop().split('?')[0] || `file_${Date.now()}`;
                
                console.log('S3Util: Direct download successful, file:', fileName, 'size:', blob.size);
                
                return new File([blob], fileName, { type: blob.type });
            }
        } catch (error) {
            console.error('Error converting file to File object:', error);
            throw error;
        }
    }

    /**
     * Validates if a file exists in S3
     * @param {string} s3Url - The S3 pre-signed URL
     * @param {string} [bucket] - S3 bucket (optional; inferred from virtual-hosted URL when omitted)
     * @returns {Promise<Object>} - File metadata
     */
    static async validateFile(s3Url, bucket = undefined) {
        try {
            const token = await AuthService.getCurrentToken();
            const encodedUrl = encodeURIComponent(s3Url);
            const bq = bucketQueryParam(s3Url, bucket);
            
            const response = await fetch(`${API_URL}/S3Util/validate?url=${encodedUrl}${bq}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error validating file in S3:', error);
            throw error;
        }
    }

    /**
     * Downloads a file directly (for browser download)
     * @param {string} s3Url - The S3 pre-signed URL
     * @param {string} fileName - Optional filename for the download
     * @param {string} [bucket] - S3 bucket (optional; inferred from virtual-hosted URL when omitted)
     */
    static async downloadFileDirect(s3Url, fileName = null, bucket = undefined) {
        try {
            const token = await AuthService.getCurrentToken();
            const encodedUrl = encodeURIComponent(s3Url);
            const bq = bucketQueryParam(s3Url, bucket);
            
            let url = `${API_URL}/S3Util/download?url=${encodedUrl}${bq}`;
            if (fileName) {
                url += `&fileName=${encodeURIComponent(fileName)}`;
            }
            
            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName || 'file');
            link.setAttribute('target', '_blank');
            
            // Add authorization header
            link.onclick = (e) => {
                e.preventDefault();
                fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                })
                .then(response => response.blob())
                .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName || 'file';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                })
                .catch(error => {
                    console.error('Error downloading file:', error);
                });
            };
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file directly:', error);
            throw error;
        }
    }
}

export default S3UtilService;
